/**
 * 工数明細の表示補助。
 *
 * 月の記録を「メンバー × プロジェクト」の行と「日」の列にピボットする。
 * ここでやるのは並べ替えと合算だけで、単価や金額には触れない
 * (金額を伴う集計は `/api/reports/*` の責務)。
 */
import type { TimeLog, TimeLogCategory } from '@/lib/api';

/** カテゴリの表記。MCP と食い違わせないため、画面側の一覧はここ 1 箇所に置く。 */
export const CATEGORIES: Array<{ value: TimeLogCategory; label: string }> = [
  { value: 'development', label: '新規開発' },
  { value: 'maintenance', label: '保守・運用' },
  { value: 'meeting', label: '会議・調整' },
  { value: 'other', label: 'その他' },
];

const CATEGORY_LABELS = Object.fromEntries(CATEGORIES.map((item) => [item.value, item.label]));

export function categoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? category;
}

/** 1 人 1 日の上限。これを超える日計は入力ミスなので目立たせる。 */
export const MAX_DAILY_HOURS = 24;

const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'];

export interface GridDay {
  /** 'YYYY-MM-DD' */
  date: string;
  /** 1..31 */
  day: number;
  /** 日曜 = 0 */
  weekday: number;
  weekdayLabel: string;
  isWeekend: boolean;
}

/**
 * 対象年月の日を並べる。
 * 曜日は UTC で求める。日付そのものは既に 'YYYY-MM-DD' に確定していて、
 * ローカル時刻を混ぜると環境によって曜日が 1 日ずれるため。
 */
export function monthDays(month: string): GridDay[] {
  const [year, mon] = month.split('-').map(Number) as [number, number];
  if (!Number.isFinite(year) || !Number.isFinite(mon)) return [];
  // Date.UTC の day に 0 を渡すと前月の末日になる
  const lastDay = new Date(Date.UTC(year, mon, 0)).getUTCDate();

  const days: GridDay[] = [];
  for (let day = 1; day <= lastDay; day += 1) {
    const date = `${month}-${String(day).padStart(2, '0')}`;
    const weekday = new Date(`${date}T00:00:00Z`).getUTCDay();
    days.push({
      date,
      day,
      weekday,
      weekdayLabel: WEEKDAY_LABELS[weekday] ?? '',
      isWeekend: weekday === 0 || weekday === 6,
    });
  }
  return days;
}

/** 「メンバー × プロジェクト × 日」の 1 マス。同じ日に複数の記録が入ることがある。 */
export interface GridCell {
  hours: number;
  logs: TimeLog[];
}

/**
 * 縦の入れ子をどちらから見るか。
 * マスが指すもの (メンバー × プロジェクト × 日) は同じで、束ね方だけが変わる。
 */
export type GridAxis = 'member' | 'project';

/**
 * 軸の選択肢。工数明細の画面と LP の見本が同じ表を読む。
 * 片方だけ語を変えると「実物と同じ部品を出している」が崩れる。
 */
export const GRID_AXES: Array<{ value: GridAxis; label: string }> = [
  { value: 'member', label: '人単位' },
  { value: 'project', label: 'プロジェクト単位' },
];

/** 1 行 = 1 人の 1 プロジェクト。`label` は内側の軸の見出し。 */
export interface GridRow {
  key: string;
  userId: string;
  userName: string;
  projectId: string;
  projectName: string;
  label: string;
  /** 日付をキーにしたマス。記録のない日はキーごと無い。 */
  cells: Record<string, GridCell>;
  total: number;
}

/** 外側の軸のまとまり。見出し行にはその日計を出す。 */
export interface GridGroup {
  /** 外側の軸の ID (メンバー単位なら user_id、プロジェクト単位なら project_id) */
  id: string;
  name: string;
  rows: GridRow[];
  dayHours: Record<string, number>;
  total: number;
}

export interface Grid {
  groups: GridGroup[];
  /** テナント全体の日計 */
  dayHours: Record<string, number>;
  total: number;
}

const collator = new Intl.Collator('ja');

export function buildGrid(logs: TimeLog[], axis: GridAxis = 'member'): Grid {
  const groups = new Map<string, GridGroup>();
  const rows = new Map<string, GridRow>();
  const dayHours: Record<string, number> = {};
  let total = 0;

  for (const log of logs) {
    const groupId = axis === 'member' ? log.userId : log.projectId;
    let group = groups.get(groupId);
    if (!group) {
      group = {
        id: groupId,
        name: axis === 'member' ? log.userName : log.projectName,
        rows: [],
        dayHours: {},
        total: 0,
      };
      groups.set(groupId, group);
    }

    const key = `${log.userId}/${log.projectId}`;
    let row = rows.get(key);
    if (!row) {
      row = {
        key,
        userId: log.userId,
        userName: log.userName,
        projectId: log.projectId,
        projectName: log.projectName,
        label: axis === 'member' ? log.projectName : log.userName,
        cells: {},
        total: 0,
      };
      rows.set(key, row);
      group.rows.push(row);
    }

    // biome-ignore lint/suspicious/noAssignInExpressions: noUncheckedIndexedAccess の下では ??= して受け取るのが最短。分けると undefined を潰すのに `!` が要る
    const cell = (row.cells[log.workedOn] ??= { hours: 0, logs: [] });
    cell.hours += log.hours;
    cell.logs.push(log);

    row.total += log.hours;
    group.dayHours[log.workedOn] = (group.dayHours[log.workedOn] ?? 0) + log.hours;
    group.total += log.hours;
    dayHours[log.workedOn] = (dayHours[log.workedOn] ?? 0) + log.hours;
    total += log.hours;
  }

  // 外側は名前順、内側は工数の多い順。締めのときに直したいのは大きい数字から。
  const sorted = [...groups.values()].sort((a, b) => collator.compare(a.name, b.name));
  for (const group of sorted) {
    group.rows.sort((a, b) => b.total - a.total || collator.compare(a.label, b.label));
    for (const row of group.rows) {
      for (const cell of Object.values(row.cells)) {
        cell.logs.sort((a, b) => a.createdAt - b.createdAt);
      }
    }
  }

  return { groups: sorted, dayHours, total };
}
